import React, { useState, useEffect } from 'react';
import { 
    Box, 
    Typography, 
    List, 
    ListItem, 
    ListItemText, 
    ListItemIcon, 
    Chip, 
    Button, 
    Divider,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Paper,
    Tooltip,
    CircularProgress
} from '@mui/material';
import { styled, alpha } from '@mui/material/styles';
import HistoryIcon from '@mui/icons-material/History';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import VerifiedIcon from '@mui/icons-material/Verified';
import CloseIcon from '@mui/icons-material/Close';
import RestoreIcon from '@mui/icons-material/Restore';
import EditIcon from '@mui/icons-material/Edit';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PersonIcon from '@mui/icons-material/Person';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import processNarrativeService from '../../services/ProcessNarrativeService';

const VersionItem = styled(ListItem)(({ theme, active }) => ({
    padding: theme.spacing(1.5, 2),
    marginBottom: theme.spacing(1),
    borderRadius: theme.spacing(1),
    transition: 'all 0.2s ease',
    backgroundColor: active ? alpha(theme.palette.primary.main, 0.1) : alpha(theme.palette.background.paper, 0.6),
    border: `1px solid ${active ? theme.palette.primary.main : theme.palette.divider}`,
    '&:hover': {
        backgroundColor: active ? alpha(theme.palette.primary.main, 0.15) : alpha(theme.palette.action.hover, 0.1),
        transform: 'translateY(-2px)',
        boxShadow: '0 4px 10px rgba(0, 0, 0, 0.05)',
    },
}));

const CompareButton = styled(Button)(({ theme }) => ({
    marginTop: theme.spacing(2),
    textTransform: 'none',
}));

/**
 * Component that displays version history for a process narrative
 * Allows viewing, comparing, and restoring previous versions
 */
const NarrativeVersionHistory = ({ 
    appId, 
    onViewVersion, 
    onCompareVersions, 
    onRestoreVersion 
}) => {
    const [versions, setVersions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedVersions, setSelectedVersions] = useState([]);
    const [compareDialogOpen, setCompareDialogOpen] = useState(false);
    const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
    
    useEffect(() => {
        const fetchVersions = async () => {
            if (!appId) return;
            
            try {
                setLoading(true);
                const data = await processNarrativeService.fetchNarrativeVersions(appId);
                setVersions(data);
            } catch (error) {
                console.error("Error fetching narrative versions:", error);
            } finally {
                setLoading(false);
            }
        };
        
        fetchVersions();
    }, [appId]);
    
    const handleVersionSelect = (version) => {
        if (selectedVersions.includes(version.id)) {
            setSelectedVersions(selectedVersions.filter(id => id !== version.id));
        } else {
            // Only allow selecting 2 versions maximum
            if (selectedVersions.length < 2) {
                setSelectedVersions([...selectedVersions, version.id]);
            } else {
                // If already 2 selected, replace the oldest selected one
                setSelectedVersions([selectedVersions[1], version.id]);
            }
        }
    };
    
    const handleCompare = () => {
        if (selectedVersions.length === 2) {
            onCompareVersions(selectedVersions[0], selectedVersions[1]);
            setCompareDialogOpen(false);
        }
    };
    
    const handleViewVersion = (versionId) => {
        onViewVersion(versionId);
        setHistoryDialogOpen(false);
    };
    
    const handleRestoreVersion = (versionId) => {
        onRestoreVersion(versionId);
        setHistoryDialogOpen(false);
    };
    
    // Format date for display
    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };
    
    const getTimeAgo = (dateString) => {
        if (!dateString) return '';
        
        const date = new Date(dateString);
        const now = new Date();
        const diffInMs = now - date;
        const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
        
        if (diffInDays === 0) {
            const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
            if (diffInHours === 0) {
                const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
                return `${diffInMinutes} minute${diffInMinutes !== 1 ? 's' : ''} ago`;
            }
            return `${diffInHours} hour${diffInHours !== 1 ? 's' : ''} ago`;
        } else if (diffInDays < 30) {
            return `${diffInDays} day${diffInDays !== 1 ? 's' : ''} ago`;
        } else if (diffInDays < 365) {
            const diffInMonths = Math.floor(diffInDays / 30);
            return `${diffInMonths} month${diffInMonths !== 1 ? 's' : ''} ago`;
        } else {
            const diffInYears = Math.floor(diffInDays / 365);
            return `${diffInYears} year${diffInYears !== 1 ? 's' : ''} ago`;
        }
    };
    
    // History dialog to show all versions
    const renderHistoryDialog = () => (
        <Dialog 
            open={historyDialogOpen} 
            onClose={() => setHistoryDialogOpen(false)}
            maxWidth="md"
            fullWidth
        >
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <HistoryIcon sx={{ mr: 1 }} />
                    Process Narrative Version History
                </Box>
                <IconButton onClick={() => setHistoryDialogOpen(false)} size="small">
                    <CloseIcon fontSize="small" />
                </IconButton>
            </DialogTitle>
            
            <DialogContent dividers>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress size={40} />
                    </Box>
                ) : versions.length === 0 ? (
                    <Typography color="textSecondary" align="center" sx={{ py: 4 }}>
                        No version history available for this narrative.
                    </Typography>
                ) : (
                    <List sx={{ width: '100%', bgcolor: 'background.paper' }}>
                        {versions.map((version, index) => (
                            <Paper 
                                key={version.id} 
                                elevation={1} 
                                sx={{ 
                                    mb: 2, 
                                    borderRadius: 2, 
                                    overflow: 'hidden',
                                    border: version.is_current ? '1px solid #4caf50' : '1px solid transparent'
                                }}
                            >
                                <Box 
                                    sx={{ 
                                        display: 'flex', 
                                        justifyContent: 'space-between', 
                                        p: 2,
                                        backgroundColor: version.is_current ? alpha('#4caf50', 0.05) : alpha('#f5f5f5', 0.5)
                                    }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                            Version {version.version_number}
                                            {version.is_current && (
                                                <Chip 
                                                    label="Current" 
                                                    size="small" 
                                                    color="success" 
                                                    sx={{ ml: 1, fontSize: '0.75rem' }} 
                                                />
                                            )}
                                        </Typography>
                                    </Box>
                                    
                                    <Box>
                                        {version.is_certified && (
                                            <Tooltip title="Certified Version">
                                                <Chip
                                                    icon={<VerifiedIcon />}
                                                    label="Certified"
                                                    size="small"
                                                    color="primary"
                                                    sx={{ mr: 1 }}
                                                />
                                            </Tooltip>
                                        )}
                                    </Box>
                                </Box>
                                
                                <Box sx={{ p: 2 }}>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <CalendarTodayIcon sx={{ fontSize: '0.9rem', mr: 0.5, color: 'text.secondary' }} />
                                            <Typography variant="body2" color="text.secondary">
                                                {formatDate(version.created_at)}
                                            </Typography>
                                        </Box>
                                        
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <PersonIcon sx={{ fontSize: '0.9rem', mr: 0.5, color: 'text.secondary' }} />
                                            <Typography variant="body2" color="text.secondary">
                                                {version.created_by}
                                            </Typography>
                                        </Box>
                                        
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <AccessTimeIcon sx={{ fontSize: '0.9rem', mr: 0.5, color: 'text.secondary' }} />
                                            <Typography variant="body2" color="text.secondary">
                                                {getTimeAgo(version.created_at)}
                                            </Typography>
                                        </Box>
                                    </Box>
                                    
                                    {version.notes && (
                                        <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
                                            "{version.notes}"
                                        </Typography>
                                    )}
                                    
                                    <Box sx={{ display: 'flex', mt: 2, justifyContent: 'flex-end' }}>
                                        <Tooltip title="View this version">
                                            <Button 
                                                size="small" 
                                                startIcon={<EditIcon />}
                                                variant="outlined"
                                                color="primary"
                                                onClick={() => handleViewVersion(version.id)}
                                                sx={{ mr: 1 }}
                                            >
                                                View
                                            </Button>
                                        </Tooltip>
                                        
                                        {!version.is_current && (
                                            <Tooltip title="Restore this version">
                                                <Button 
                                                    size="small" 
                                                    startIcon={<RestoreIcon />}
                                                    variant="outlined"
                                                    color="secondary"
                                                    onClick={() => handleRestoreVersion(version.id)}
                                                >
                                                    Restore
                                                </Button>
                                            </Tooltip>
                                        )}
                                    </Box>
                                </Box>
                            </Paper>
                        ))}
                    </List>
                )}
                
                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
                    <Button
                        variant="outlined"
                        startIcon={<CompareArrowsIcon />}
                        onClick={() => {
                            setHistoryDialogOpen(false);
                            setCompareDialogOpen(true);
                        }}
                    >
                        Compare Versions
                    </Button>
                </Box>
            </DialogContent>
            
            <DialogActions>
                <Button onClick={() => setHistoryDialogOpen(false)}>Close</Button>
            </DialogActions>
        </Dialog>
    );
    
    // Dialog for selecting versions to compare
    const renderCompareDialog = () => (
        <Dialog 
            open={compareDialogOpen} 
            onClose={() => setCompareDialogOpen(false)}
            maxWidth="md"
            fullWidth
        >
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <CompareArrowsIcon sx={{ mr: 1 }} />
                    Compare Narrative Versions
                </Box>
                <IconButton onClick={() => setCompareDialogOpen(false)} size="small">
                    <CloseIcon fontSize="small" />
                </IconButton>
            </DialogTitle>
            
            <DialogContent dividers>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                    Select two versions to compare. The differences between these versions will be highlighted.
                </Typography>
                
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress size={40} />
                    </Box>
                ) : versions.length === 0 ? (
                    <Typography color="textSecondary" align="center" sx={{ py: 4 }}>
                        No version history available for this narrative.
                    </Typography>
                ) : (
                    <List sx={{ width: '100%' }}>
                        {versions.map((version) => (
                            <VersionItem 
                                key={version.id} 
                                active={selectedVersions.includes(version.id)}
                                onClick={() => handleVersionSelect(version)}
                                button
                            >
                                <ListItemIcon sx={{ minWidth: 40 }}>
                                    {selectedVersions.includes(version.id) && selectedVersions.indexOf(version.id) === 0 && (
                                        <Chip size="small" label="A" color="primary" />
                                    )}
                                    {selectedVersions.includes(version.id) && selectedVersions.indexOf(version.id) === 1 && (
                                        <Chip size="small" label="B" color="secondary" />
                                    )}
                                    {!selectedVersions.includes(version.id) && (
                                        <HistoryIcon color="action" fontSize="small" />
                                    )}
                                </ListItemIcon>
                                <ListItemText
                                    primary={
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <Typography variant="body1" component="span" sx={{ fontWeight: 500 }}>
                                                Version {version.version_number}
                                            </Typography>
                                            {version.is_certified && (
                                                <Tooltip title="Certified Version">
                                                    <VerifiedIcon sx={{ ml: 1, color: 'primary.main', fontSize: '0.9rem' }} />
                                                </Tooltip>
                                            )}
                                            {version.is_current && (
                                                <Chip 
                                                    label="Current" 
                                                    size="small" 
                                                    color="success" 
                                                    sx={{ ml: 1, height: 20, fontSize: '0.7rem' }} 
                                                />
                                            )}
                                        </Box>
                                    }
                                    secondary={
                                        <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                                            <Typography variant="body2" component="span" color="textSecondary">
                                                {formatDate(version.created_at)} by {version.created_by}
                                            </Typography>
                                        </Box>
                                    }
                                />
                            </VersionItem>
                        ))}
                    </List>
                )}
                
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                    <CompareButton
                        variant="contained"
                        color="primary"
                        startIcon={<CompareArrowsIcon />}
                        onClick={handleCompare}
                        disabled={selectedVersions.length !== 2}
                    >
                        Compare Selected Versions
                    </CompareButton>
                </Box>
            </DialogContent>
            
            <DialogActions>
                <Button onClick={() => setCompareDialogOpen(false)}>Cancel</Button>
            </DialogActions>
        </Dialog>
    );
    
    return (
        <>
            <Button
                variant="outlined"
                color="primary"
                startIcon={<HistoryIcon />}
                onClick={() => setHistoryDialogOpen(true)}
                size="small"
            >
                Version History
            </Button>
            
            {renderHistoryDialog()}
            {renderCompareDialog()}
        </>
    );
};

export default NarrativeVersionHistory; 